import React, { FC, useState } from 'react'
import { capitalize } from 'lodash'
import { makeStyles } from '@material-ui/core/styles'
import {
  Theme,
  IconButton,
  Tooltip,
  ClickAwayListener,
  TooltipProps,
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import { ProductConfiguration } from '@src/types/graphql-types'
import { FormattedMessage, useIntl } from 'react-intl'
import messages from '@utils/messages'
// Semi and Config both of the _PC_SPEC keys but semi is smaller
import configMessages from '@src/routes/SemiConfigurators/messages'
import { checkValidKey } from '@src/routes/Configurators/utils/util'
import LinkStyleButton from '@src/components/LinkStyleButton/LinkStyleButton'

const useStyles = makeStyles((theme: Theme) => ({
  tooltip: {
    padding: theme.spacing(4),
    minHeight: 100,
    minWidth: 250,
    maxWidth: 600,
    background: theme.palette.background.default,
    color: theme.palette.text.primary,
    fontSize: theme.typography.fontSize,
    fontWeight: theme.typography.fontWeightRegular,
    boxShadow: theme.shadows[4],
  },
  arrow: {
    color: theme.palette.background.default,
  },
  // Override how the arrow renders so the arrow also has an accurate box-shadow
  popperArrow: {
    '&[x-placement] $arrow::before': {
      borderColor: 'transparent transparent currentcolor currentcolor',
      borderWidth: '1em',
      boxShadow: `-3px 3px 3px 0 ${theme.palette.divider}`,
      transformOrigin: '0 0',
    },
    '&[x-placement^="top"] $arrow::before': {
      transformOrigin: '0 0',
      transform: 'translate(0px, 0px) rotate(-45deg)',
    },
    '&[x-placement^="left"] $arrow::before': {
      transform: 'translate(0px, 16px) rotate(-135deg)',
    },
    '&[x-placement^="right"] $arrow::before': {
      transform: 'translate(6px, 0px) rotate(45deg)',
    },
    // Left-end/right-end is an edge case because of how the arrow overhangs the bottom unless transformed into place
    '&[x-placement="left-end"] $arrow::before': {
      transform: 'translate(-2px, 14px) rotate(-135deg)',
    },
    '&[x-placement="right-end"] $arrow::before': {
      transform: 'translate(8px, -2px) rotate(45deg)',
    },
    '&[x-placement^="bottom"] $arrow::before': {
      // Make shadow slightly more diffuse to match the styling of the top shadow
      boxShadow: `-3px 3px 5px -2px ${theme.palette.divider}`,
      transform: 'translate(0px, 6px) rotate(135deg)',
    },
  },
  closeButton: {
    position: 'absolute',
    top: 0,
    right: 0,
  },
  row: {
    wordBreak: 'break-all',
    '&:first-of-type': {
      // Give ample margin to the right of the first item so the close button won't overlap
      // 4 is derived from: icon btn width (6, i.e. 48px) - padding (2, i.e. 16px) == 4 i.e. 32px.
      marginRight: theme.spacing(4),
    },
    '&:not(:last-of-type)': {
      marginBottom: theme.spacing(1),
    },
  },
}))

interface CartItemConfigurationPopoverProps {
  /** Can be used for aria-describedby */
  id?: string
  open: boolean
  setOpen: (open: boolean) => void
  materialNumber: string
  configuration: Pick<ProductConfiguration, 'name' | 'value'>[]
  placement?: TooltipProps['placement']
  children: React.ReactElement<any, any>
}

enum ConfigurationValueTypes {
  Purification = 'purification',
  Format = 'format',
  Scale = 'scale',
  Concentration = 'concentration',
  Modification = 'modification',
  Services = 'services',
  Amount = 'amount',
  IscaleProduct = 'VC00035N',
  Sequence = 'sequence',
}

const GetServiceAdditionalMessage = (item) => {
  switch (item.trim()) {
    case 'ANN':
      return messages.CONF_NGSO_ADDITIONAL_SERVICES_ANNEALING
    case 'ANP':
      return messages.CONF_NGSO_ADDITIONAL_SERVICES_ANNEALING_POOLING
    case 'AQC':
      return messages.CONF_NGSO_ADDITIONAL_SERVICES_ANNEALING_QC
    default:
      return {
        ...checkValidKey(configMessages[`PC_SPEC_${item.trim()}`], `${item}`),
      }
  }
}

const CartItemConfigurationPopover: FC<CartItemConfigurationPopoverProps> = ({
  id,
  open,
  setOpen,
  materialNumber,
  configuration,
  placement = 'top-start',
  children,
}) => {
  const classes = useStyles()
  const intl = useIntl()

  const setConfigurationValue = (name: string, value?: Maybe<string>) => {
    switch (name) {
      case ConfigurationValueTypes.Purification:
        return <FormattedMessage {...configMessages[`_PC_SPEC_${value}`]} />

      case ConfigurationValueTypes.Format:
        if (value === 'D' || value === 'DRY') {
          return <FormattedMessage {...configMessages._PC_SPEC_DRY} />
        } else if (value && value.toUpperCase() === 'TE') {
          return <FormattedMessage {...configMessages._PC_SPEC_IS_TE} />
        } else if (value && value.toUpperCase() === 'TRIS') {
          return <FormattedMessage {...configMessages._PC_SPEC_IS_TRIS} />
        } else if (value && value.toUpperCase() === 'WATER') {
          return <FormattedMessage {...configMessages._PC_SPEC_IS_WATER} />
        }
        //Format for In Solution is 'S' for NOW countries and 'LIQUID' for NEXT countries
        else if (value === 'S' || value === 'LIQUID') {
          const buffer = configuration.filter(({ name }) => name === 'buffer')
          if (buffer?.length) {
            let formatSuffix = buffer[0]?.value
            if (formatSuffix?.includes('-')) {
              formatSuffix = formatSuffix.split('-')[1]
            }
            return <FormattedMessage {...messages[`${formatSuffix}`]} />
          } else {
            return <FormattedMessage {...messages.LIQUID} />
          }
        } else if (value === 'INSOL') {
          return <FormattedMessage {...messages.INSOL} />
        } else if (value === 'INSOL40') {
          return <FormattedMessage {...messages.INSOL40} />
        } else if (value === 'INSOLCSR') {
          return <FormattedMessage {...messages.INSOLCSR} />
        } else {
          return value
        }

      case ConfigurationValueTypes.Scale:
        let scale = value
        // if scale is not decimal, it is a semi-configurator product.
        if (scale?.indexOf('.') !== -1) {
          //configurator product
          scale = scale?.replace(/[^a-zA-Z0-9]/g, '_')
          scale = scale?.includes('UMO') ? scale : `${scale}_UMO`
          return configMessages[`_PC_SPEC_${scale}`] ? (
            <FormattedMessage {...configMessages[`_PC_SPEC_${scale}`]} />
          ) : (
            value
          )
        } else {
          //semi-configurator product
          scale += intl.formatMessage(messages.CONF_DUPLEX_AMOUNT_MOLES)
          return scale
        }

      case ConfigurationValueTypes.Concentration:
        let concentration = value
        if (value?.includes('um')) {
          concentration = concentration?.replace('um', 'UM')
          // Check if key is present for dynamic values..
          return configMessages[`_PC_SPEC_${concentration}`] ? (
            <FormattedMessage
              {...configMessages[`_PC_SPEC_${concentration}`]}
            />
          ) : (
            value
          )
        } else {
          concentration += intl.formatMessage(
            messages.CONF_DNAPLATES_CONCENTRATION_MEASURE
          )
          return concentration
        }

      case ConfigurationValueTypes.Modification:
        return value === 'Y' ? (
          <FormattedMessage {...messages.YES} />
        ) : value === 'N' ? (
          <FormattedMessage {...messages.NO} />
        ) : (
          value
        )

      case ConfigurationValueTypes.Services:
        const data = value?.split(',')
        if (data) {
          const services: any = []
          data.map((item) => {
            return services.push(
              intl.formatMessage(GetServiceAdditionalMessage(item))
            )
          })
          return services.join(', ')
        } else {
          return value
        }

      case ConfigurationValueTypes.Amount:
        if (materialNumber === ConfigurationValueTypes.IscaleProduct) {
          return (
            value + intl.formatMessage(messages.CONF_ISCALE_DNA_AMOUNT_MEASURE)
          )
        } else {
          return value
        }
      default:
        return value
    }
  }

  const setConfigurationName = (name: string) => {
    const nameConfiguration = {
      acceptorsequence: 'Acceptor Sequence',
      aliquoting: 'Aliquoting',
      amount: 'Amount',
      analyticalHPLC: 'Analytical HPLC',
      analyticalHplc: 'Analytical HPLC',
      animalType: 'Animal Type',
      antisenceSequence: 'Antisense Sequence',
      antisenseName: 'Antisense Name',
      antisenseOverhang: 'Antisense Overhang',
      antisenseSequence: 'Antisense Sequence',
      assayId: 'Assay ID',
      carrierProtein: 'Carrier Protein',
      certificateOfAnalysis: 'COA',
      columnConstruction: 'Column Construction',
      comments: 'Comments',
      concentration: 'Concentration',
      configid: 'Config ID',
      conjugationAmount: 'Conjugation Amount',
      countOfWells: 'Total Duplex Count',
      crisprDesignName: 'CRISPR Design Name',
      cterminalMod: 'C-Terminal Mod',
      customPremixing: 'Custom Premixing',
      donorSequence: 'Donor Sequence',
      elisaAnalysis: 'ELISA Analysis',
      fiveMod: "5' Mod",
      format: 'Format',
      geneId: 'Gene ID',
      geneSymbol: 'Gene Symbol',
      immunizationTerm: 'Immunization term',
      lentiviralBackbone: 'Lentiviral Backbone',
      modification: 'Modification',
      ngsoType: 'NGSO Type',
      noOfAminoAcids: 'No. of Amino Acids',
      noOfBases: 'No. of Bases',
      noOfTubes: 'No. of Tubes',
      nterminalMod: 'N-Terminal Mod',
      numberOfAnimals: 'Number of Animals',
      numberofAnimals: 'Number of Animals',
      oligo1Name: 'Oligo 1 Name',
      oligo2Name: 'Oligo 2 Name',
      oligoName: 'Oligo Name',
      panelName: 'Panel Name',
      peptideName: 'Peptide Name',
      peptideSequence: 'Peptide Sequence',
      physicalMaterial: 'Physical Material',
      plateType: 'Plate Type',
      primerPairId: 'Primer Pair ID',
      productId: 'Product Id',
      projectName: 'Project Name',
      purification: 'Purification',
      purificationColumn: 'Purification Column',
      purificationOption: 'Purification Option',
      purificationType: 'Purification Type',
      purityLevel: 'Purity Level',
      quantitation: 'Quantitation',
      refSeqId: 'RefSeq ID',
      researchAreas: 'Research Areas',
      revisionid: 'Revision Number',
      sampleType: 'Sample Type',
      scale: 'Scale',
      selectedAnalytes: 'Analytes',
      senseName: 'Sense Name',
      senseOverhang: 'Sense Overhang',
      senseSequence: 'Sense Sequence',
      Sequence: 'Sequence',
      spacesaverOption: 'Spacesaver Option',
      species: 'Species',
      structure: 'Structure',
      targetCloneId: 'Target Clone ID',
      targetDNASequence: 'Target DNA Sequence',
      targetedGene: 'Targeted Gene',
      threeMod: "3' Mod",
      titer: 'Titer',
      totalBaseCount: 'Total Base Count',
      trcCloneId: 'TRC Clone ID',
      trcVersion: 'TRC Version',
      validated: 'Validated',
      vector: 'Vector',
      viralTiter: 'Viral Titer',
      volume: 'Volume',
      wellType: 'Well Type',
    }

    if (nameConfiguration[name]) {
      return nameConfiguration[name]
    } else {
      return name.replace(/\w+/g, capitalize)
    }
  }

  const getPhysicalMaterialKey = [
    {
      key: 'pLKO.1',
      value: 'pLKO.1',
    },
    {
      key: '-CMV-Neo',
      value: 'pLKO.1-CMV-Neo',
    },
    {
      key: '-hPGK-Neo',
      value: 'pLKO.1-Neo',
    },
    {
      key: '-CMV-tGFP',
      value: 'pLKO.1-CMV-tGFP',
    },
    {
      key: '-hPGK-Neo-CMV-tGFP',
      value: 'pLKO.1-Neo-CMV-tGFP',
    },
    {
      key: '-hPGK-Puro-CMV-tGFP',
      value: 'pLKO.1-puro-CMV-tGFP',
    },
  ]

  const mapSHCLNDProductKey = (value) => {
    const data = getPhysicalMaterialKey.filter(
      (item) => item.key.toUpperCase() === value.toUpperCase()
    )
    const key = data[0] && data[0].value ? data[0].value : value
    return key.toUpperCase()
  }

  return (
    <ClickAwayListener onClickAway={() => open && setOpen(false)}>
      <Tooltip
        id={id}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        placement={placement}
        classes={{
          tooltip: classes.tooltip,
          arrow: classes.arrow,
          popperArrow: classes.popperArrow,
        }}
        disableFocusListener
        disableHoverListener
        disableTouchListener
        interactive
        arrow
        title={
          <>
            <IconButton
              aria-label="close"
              className={classes.closeButton}
              onClick={() => setOpen(false)}
            >
              <CloseIcon color="secondary" />
            </IconButton>
            {configuration
              .filter(
                (conf): conf is { name: string; value?: Maybe<string> } =>
                  !!conf.name
              )
              .filter(({ name }) => name !== 'name')
              .map(({ name, value }) =>
                value !== null && value !== '' ? (
                  <div
                    key={`${materialNumber}_conf_${name}`}
                    className={classes.row}
                  >
                    <strong>{setConfigurationName(name)}: </strong>
                    {(materialNumber === 'SHCLND' ||
                      materialNumber === 'SHCLNV') &&
                    name === 'vector'
                      ? mapSHCLNDProductKey(setConfigurationValue(name, value))
                      : setConfigurationValue(name, value)}
                  </div>
                ) : null
              )}
          </>
        }
      >
        {children}
      </Tooltip>
    </ClickAwayListener>
  )
}

type UncontrolledCartItemConfigurationPopoverProps = Omit<
  CartItemConfigurationPopoverProps,
  'open' | 'setOpen'
> & {
  buttonClassName?: string
}

export const UncontrolledCartItemConfigurationPopover: FC<
  UncontrolledCartItemConfigurationPopoverProps
> = ({ id, children, buttonClassName, ...rest }) => {
  const [open, setOpen] = useState(false)
  return (
    <CartItemConfigurationPopover
      id={open ? id : undefined}
      open={open}
      setOpen={setOpen}
      {...rest}
    >
      <LinkStyleButton
        className={buttonClassName}
        onClick={() => setOpen(!open)}
      >
        {children}
      </LinkStyleButton>
    </CartItemConfigurationPopover>
  )
}

export default CartItemConfigurationPopover
